import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Send, Loader2, Mail, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";

export default function Kontakt() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "Allgemeine Anfrage", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Nachricht gesendet", {
        description: "Vielen Dank! Der Verein meldet sich in Kürze bei Ihnen.",
      });
      setForm({ name: "", email: "", subject: "Allgemeine Anfrage", message: "" });
    } catch {
      toast.error("Senden fehlgeschlagen", {
        description: "Bitte versuchen Sie es erneut oder schreiben Sie an info@kga-schoenwald.de.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-16 md:py-28 bg-[#EFECE4] border-t border-[#D8D3C5]" data-testid="kontakt-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="10"
          eyebrow="Kontakt"
          title="Schreiben Sie uns"
          text="Fragen zur Anlage, zu Rechnungen oder zum Verein? Nutzen Sie das Formular – oder schreiben Sie direkt eine Mail."
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-2xl border border-[#D8D3C5] bg-white p-6 sm:p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Name *</label>
                  <input required className={FIELD} value={form.name} onChange={set("name")} placeholder="Ihr Name" data-testid="contact-input-name" />
                </div>
                <div>
                  <label className={LABEL}>E-Mail *</label>
                  <input required type="email" className={FIELD} value={form.email} onChange={set("email")} placeholder="ihre@mail.de" data-testid="contact-input-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Betreff</label>
                  <select className={FIELD} value={form.subject} onChange={set("subject")} data-testid="contact-input-subject">
                    <option>Allgemeine Anfrage</option>
                    <option>Wasser & Abwasser</option>
                    <option>Arbeitseinsätze</option>
                    <option>Vorstand</option>
                    <option>Website</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL}>Nachricht *</label>
                  <textarea required rows={6} className={FIELD} value={form.message} onChange={set("message")} placeholder="Ihre Nachricht an den Verein …" data-testid="contact-input-message" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Nachricht senden
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl border border-[#D8D3C5] bg-white p-7" data-testid="contact-mail-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                <Mail size={19} strokeWidth={1.8} />
              </span>
              <h3 className="mt-4 font-display text-xl font-semibold text-[#1A382B]">Direkt per Mail</h3>
              <p className="mt-2 text-sm sm:text-base text-[#47544A]">
                Verein: <a href="mailto:info@kga-schoenwald.de" className="font-medium text-[#1A382B] underline decoration-[#C85A32]/50 underline-offset-4 hover:text-[#C85A32]" data-testid="contact-mail-info">info@kga-schoenwald.de</a>
              </p>
              <p className="mt-1.5 text-sm sm:text-base text-[#47544A]">
                Wasser: <a href="mailto:Wasser@kga-schoenwald.de" className="font-medium text-[#1A382B] underline decoration-[#C85A32]/50 underline-offset-4 hover:text-[#C85A32]" data-testid="contact-mail-wasser">wasser@kga-schoenwald.de</a>
              </p>
            </div>
            <div className="rounded-2xl border border-[#D8D3C5] bg-white p-7" data-testid="contact-website-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                <Globe size={19} strokeWidth={1.8} />
              </span>
              <h3 className="mt-4 font-display text-xl font-semibold text-[#1A382B]">Anregungen zur Website</h3>
              <p className="mt-2 text-sm sm:text-base text-[#47544A]">
                Sorgen, Wünsche und Anregungen bezüglich der Website bitte an{" "}
                <a href="mailto:admin@kga-schoenwald.de" className="font-medium text-[#1A382B] underline decoration-[#C85A32]/50 underline-offset-4 hover:text-[#C85A32]" data-testid="contact-mail-admin">admin@kga-schoenwald.de</a>.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[#6F7E73]">
                Hinweis: Für die Verwaltung der Grundstücke ist der Verein nicht zuständig – bitte wenden
                Sie sich an das KVA Berlin Mitte Nord, Herr Wittig, Tel. 030 2581 85 226.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
